<template>
  <v-app
    :theme="currentTheme"
    :data-drawer-open="drawer && !mobile"
    :data-wide-layout="isWideLayout"
    :data-has-right-rail="!isWideLayout && showRightSidebar"
  >
    <!-- App Loading Screen - skeleton that matches the app shell layout to prevent CLS -->
    <Transition name="app-loading-fade">
      <div v-if="!appReady" class="app-loading" key="loading">
        <!-- Error State -->
        <div v-if="appLoadError" class="app-loading__error">
          <v-icon size="64" color="error" class="app-loading__error-icon">
            mdi-alert-circle-outline
          </v-icon>
          <h2 class="app-loading__error-title">Unable to Load App</h2>
          <p class="app-loading__error-message">{{ appLoadError }}</p>
          <button
            class="app-loading__retry-btn"
            :disabled="isRetrying"
            @click="retryAppLoad"
          >
            <v-icon
              v-if="isRetrying"
              size="18"
              class="app-loading__retry-spinner"
            >
              mdi-loading
            </v-icon>
            <span v-else>Try Again</span>
          </button>
        </div>
        <!-- Skeleton Loading State - mirrors app shell layout to prevent CLS -->
        <template v-else>
          <div class="app-loading__skeleton-header" />
          <div class="app-loading__skeleton-body">
            <div class="app-loading__spinner" />
          </div>
          <div class="app-loading__skeleton-bottom-bar" />
        </template>
      </div>
    </Transition>

    <!-- Main App Content (hidden until settings load) -->
    <template v-if="appReady">
      <!-- Premium Background -->
      <div class="app-background" />

      <!-- Wrapper for single-post mode stacking -->
      <div
        class="app-wrapper"
        :class="{ 'app-wrapper--single-post': useStackedViewportMain }"
      >
        <!-- App Container - centers everything on wide screens -->
        <div
          class="app-container"
          :class="{ 'app-container--drawer-open': drawer && !mobile }"
        >
          <!-- Header (hidden on detail pages for mobile) -->
          <AppHeader
            v-if="!isDetailPage"
            @toggle-drawer="drawer = !drawer"
            @open-search="showSearch = true"
            @open-settings="showSettings = true"
            @open-discover="showMobileDiscover = true"
            @open-terms="showTermsModal = true"
            @open-privacy="showPrivacyModal = true"
          />

          <!-- Left Sidebar -->
          <AppSidebar
            v-model="drawer"
            :rail="rail"
            @open-settings="showSettings = true"
            @open-invite="showInviteModal = true"
          />

          <!-- Main Content -->
          <main
            ref="appMainRef"
            class="app-main"
            :class="{
              'app-main--drawer-open': drawer && !mobile,
              'app-main--drawer-collapsed': !drawer && !mobile,
              'app-main--has-bottom-tab': reserveBottomTabSpaceInShell,
              'app-main--manages-bottom-chrome': managesOwnBottomChrome,
              'app-main--single-post': useStackedViewportMain,
              'app-main--has-feed-insets': hasFeedInsets,
              'app-main--search-subheader': isSearchRouteWithSubheader,
            }"
          >
            <div
              class="app-layout"
              :class="{
                'app-layout--wide': isWideLayout,
                'app-layout--has-player': hasAudioPlayer,
              }"
            >
              <!-- Center Content -->
              <div ref="centerScrollRef" class="app-layout__center">
                <router-view v-slot="{ Component, route }">
                  <!-- Keep the fade, but don't gate enter on leave completion.
                       Discover's fixed reel shell can otherwise leave router-view empty on
                       `/discover -> /timeline` transitions even though the route already changed. -->
                  <Transition name="page-fade">
                    <component :is="Component" :key="route.name" />
                  </Transition>
                </router-view>
              </div>

              <!-- Right Sidebar (desktop only; includes Trending Now on the right) -->
              <div
                v-if="!isWideLayout && showRightSidebar"
                class="app-layout__right d-none d-lg-block"
              >
                <RightSidebar
                  @filter-change="handleFilterChange"
                  @open-settings="showSettings = true"
                />
              </div>
            </div>
          </main>
        </div>

        <!-- Bottom Tab Bar (mobile only) -->
        <BottomTabBar
          v-if="mobile && !hideBottomTabBar && route.name !== 'login'"
          :is-single-post-mode="isSinglePostFeedLayout"
        />
      </div>

      <!-- Settings Dialog -->
      <Teleport to="body">
        <Transition name="modal">
          <div
            v-if="showSettings"
            class="modal-overlay"
            @click.self="closeSettingsModal"
          >
            <v-theme-provider :theme="settingsModalTheme" with-background>
              <div
                ref="settingsModalRef"
                class="modal-container settings-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="settings-modal-title"
                tabindex="-1"
                @click.stop
              >
                <header class="settings-modal__header">
                  <h2 id="settings-modal-title" class="settings-modal__title">
                    <v-icon size="20">mdi-cog-outline</v-icon>
                    Settings
                  </h2>
                  <button
                    ref="settingsCloseButtonRef"
                    class="settings-modal__close"
                    type="button"
                    aria-label="Close settings"
                    @click="closeSettingsModal"
                  >
                    <v-icon size="20">mdi-close</v-icon>
                  </button>
                </header>

                <div class="settings-modal__content">
                  <!-- Theme Selection (only show when uiMode is 'both') -->
                  <template v-if="appSettings.uiMode.value === 'both'">
                    <section class="settings-section">
                      <h3 class="settings-section__title">Appearance</h3>
                      <p class="settings-section__desc">
                        Choose your preferred theme
                      </p>
                      <div class="settings-theme-toggle">
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'light',
                          }"
                          type="button"
                          :aria-pressed="uiStore.themeMode === 'light'"
                          @click="uiStore.setTheme('light')"
                        >
                          <v-icon size="18">mdi-white-balance-sunny</v-icon>
                          Light
                        </button>
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'dark',
                          }"
                          type="button"
                          :aria-pressed="uiStore.themeMode === 'dark'"
                          @click="uiStore.setTheme('dark')"
                        >
                          <v-icon size="18">mdi-moon-waning-crescent</v-icon>
                          Dark
                        </button>
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'system',
                          }"
                          type="button"
                          :aria-pressed="uiStore.themeMode === 'system'"
                          @click="uiStore.setTheme('system')"
                        >
                          <v-icon size="18">mdi-theme-light-dark</v-icon>
                          System
                        </button>
                      </div>
                    </section>

                    <div class="settings-divider" />
                  </template>

                  <!-- Developer Token -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">Developer Token</h3>
                    <p class="settings-section__desc">
                      Set a token directly for testing purposes
                    </p>
                    <div class="settings-token">
                      <input
                        v-model="developerToken"
                        type="password"
                        placeholder="Enter access token"
                        class="settings-token__input"
                      />
                      <button
                        class="settings-token__btn"
                        type="button"
                        :disabled="!developerToken.trim() || isSettingToken"
                        @click="handleSetDeveloperToken"
                      >
                        {{ isSettingToken ? "Setting..." : "Set Token" }}
                      </button>
                    </div>
                    <div v-if="tokenError" class="settings-error">
                      <v-icon size="16">mdi-alert-circle</v-icon>
                      {{ tokenError }}
                    </div>
                  </section>

                  <div class="settings-divider" />

                  <!-- Invite Friends -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">Invite Friends</h3>
                    <p class="settings-section__desc">
                      Share an invite link to bring friends to the app.
                    </p>
                    <div class="settings-token">
                      <input
                        :value="invite.inviteLink.value"
                        type="text"
                        readonly
                        class="settings-token__input"
                        aria-label="Invite link"
                      />
                      <button
                        class="settings-token__btn"
                        type="button"
                        @click="handleCopyInvite"
                      >
                        Copy
                      </button>
                      <button
                        class="settings-token__btn"
                        type="button"
                        @click="handleShareInvite"
                      >
                        Share
                      </button>
                    </div>
                    <div class="settings-api-url mt-3">
                      <code>Shared {{ invite.totalShares.value }} time(s)</code>
                    </div>
                  </section>

                  <div class="settings-divider" />

                  <!-- API Configuration -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">API Configuration</h3>
                    <p class="settings-section__desc">
                      Base URL from environment
                    </p>
                    <div class="settings-api-url">
                      <code>{{ apiBaseUrl }}</code>
                    </div>
                  </section>
                </div>
              </div>
            </v-theme-provider>
          </div>
        </Transition>
      </Teleport>

      <!-- Mobile Search Dialog -->
      <Teleport to="body">
        <Transition name="modal">
          <div
            v-if="showSearch"
            class="modal-overlay modal-overlay--fullscreen"
            @click.self="closeSearchModal"
          >
            <v-theme-provider :theme="currentTheme" with-background>
              <div
                ref="searchModalRef"
                class="search-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="search-modal-title"
                tabindex="-1"
                @click.stop
              >
                <header class="search-modal__header">
                  <h2 id="search-modal-title" class="sr-only">Search</h2>
                  <button
                    class="search-modal__back"
                    type="button"
                    aria-label="Close search"
                    @click="closeSearchModal"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                  </button>
                  <SearchInputBase
                    ref="mobileSearchInputRef"
                    placeholder="Search posts, songs and users"
                    :navigate-on-enter="true"
                    class="search-modal__search-input"
                    @search="handleMobileSearch"
                  />
                </header>
                <div class="search-modal__content">
                  <p class="search-modal__hint">
                    Type to search for posts, songs, users and hashtags
                  </p>
                </div>
              </div>
            </v-theme-provider>
          </div>
        </Transition>
      </Teleport>

      <!-- Audio Player - Teleported to body to escape v-application__wrap's backface-visibility: hidden
           which creates a containing block and breaks position: fixed -->
      <Teleport to="body">
        <v-theme-provider :theme="currentTheme" with-background>
          <AudioPlayer ref="audioPlayerRef" />
        </v-theme-provider>
      </Teleport>

      <!-- Video Player -->
      <VideoPlayer ref="videoPlayerRef" />

      <!-- Comment Modal -->
      <CommentModal v-if="isCommentModalMounted" ref="commentModalRef" />

      <!-- Create Post Modal -->
      <CreatePostModal
        v-if="isCreatePostModalMounted"
        ref="createPostModalRef"
      />

      <!-- Create Song Modal -->
      <CreateSongModal
        v-if="isCreateSongModalMounted"
        ref="createSongModalRef"
        @close="closeCreateSong"
      />

      <!-- TanStack Query DevTools (gated by env var) -->
      <VueQueryDevtools v-if="showQueryDevtools" />

      <!-- Resume incomplete uploads from prior session -->
      <ResumeUploadPrompt />

      <!-- PWA Install/Update Prompts -->
      <PWAPrompt />

      <!-- Push Notification Permission Prompt -->
      <NotificationPrompt />

      <!-- Update Notification Modal -->
      <UpdateNotificationModal
        v-if="showUpdateModal"
        v-model="showUpdateModal"
        :title="updateTitle"
        :message="updateMessage"
        :features="updateFeatures"
        :auto-reload="false"
        @update="handleAppUpdate"
        @later="handleAppUpdateLater"
      />

      <!-- Mobile Discover Sheet (trending + who to follow) -->
      <MobileDiscoverSheet
        v-if="showMobileDiscover"
        v-model="showMobileDiscover"
      />

      <!-- Mobile Filter Sheet (content type filters) -->
      <MobileFilterSheet
        v-if="showMobileFilter"
        v-model="showMobileFilter"
        :selected-filter="feedFilter"
        @filter-change="handleFilterChange"
      />

      <!-- Legal Modals (Terms and Privacy) -->
      <PrivacyPolicyModal v-if="showPrivacyModal" v-model="showPrivacyModal" />
      <TermsOfServiceModal v-if="showTermsModal" v-model="showTermsModal" />

      <!-- Invite Friends Modal (must be outside sidebar to avoid transform containment on iOS) -->
      <!-- Gate the async mount on auth only; let the dialog's own v-model + persistent
           drive visibility. Tying v-if to showInviteModal let a stray overlay reset
           destroy the modal mid-open on iOS, so it couldn't stay open. -->
      <InviteFriendsModal v-if="isAuthenticated" v-model="showInviteModal" />

      <!-- Action Feedback Overlay -->
      <ActionFeedbackOverlay
        :show="feedback.show"
        :icon="feedback.icon"
        :text="feedback.text"
        :type="feedback.type"
      />

      <v-snackbar
        v-model="chunkRecoveryToastVisible"
        :timeout="-1"
        location="bottom"
        color="surface-variant"
        class="chunk-recovery-snackbar"
      >
        Updating app...
      </v-snackbar>

      <!-- Global Recording Button (for Feedback) -->
      <GlobalRecordingButton v-if="showGlobalRecordingButton" />
    </template>
  </v-app>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  defineAsyncComponent,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  shallowRef,
  watch,
  nextTick,
  type Ref,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { useTheme, useDisplay } from "vuetify";
import { useQueryClient } from "@tanstack/vue-query";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useAuth } from "@/composables/useAuth";
import { authService } from "@/services/authService";
import { useAppSettings } from "@/composables/useAppSettings";
import { isFirstPage } from "@/composables/useNavigationHistory";
import { useThemeColors } from "@/composables/useThemeColors";
import { initAnalytics, trackPageView } from "@/composables/useAnalytics";
import { usePWA } from "@/composables/usePWA";
import { debounce } from "@/utils/debounce";
import { useUpdateNotification } from "@/composables/useUpdateNotification";
import { useInviteFriends } from "@/composables/useInviteFriends";
import { storeToRefs } from "pinia";
import { useUiStore } from "@/stores/ui";
import { useAppModeStore } from "@/stores/appMode";
import { useProcessingPostsStore } from "@/stores/processingPosts";
import { updateGroupInCache } from "@/queries/useMessagesQueries";
import { queryKeys } from "@/queries/queryKeys";
import { useSdk } from "@/composables/useSdk";
import { useAudioPlayer } from "@/composables/useAudioPlayer";
import { useNetworkStatus } from "@/composables/useNetworkStatus";
import { useDeepLinking } from "@/composables/useDeepLinking";
import { useDialogA11y } from "@/composables/useDialogA11y";
import AppHeader from "@/components/layout/AppHeader.vue";
import SearchInputBase from "@/components/search/SearchInputBase.vue";
import AppSidebar from "@/components/layout/AppSidebar.vue";
import RightSidebar from "@/components/layout/RightSidebar.vue";
import BottomTabBar from "@/components/layout/BottomTabBar.vue";
import AudioPlayer from "@/components/player/AudioPlayer.vue";
import VideoPlayer from "@/components/player/VideoPlayer.vue";
import PWAPrompt from "@/components/pwa/PWAPrompt.vue";
import ResumeUploadPrompt from "@/components/upload/ResumeUploadPrompt.vue";
import NotificationPrompt from "@/components/pwa/NotificationPrompt.vue";
import OfflineIndicator from "@/components/OfflineIndicator.vue";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import screenRecordingService from "@/services/screenRecordingService";
import type { FeedFilter, OpenCreatePostOptions, Post } from "@/types";
import type {
  ChatUnreadCountEvent,
  PostProcessingCompleteEvent,
} from "@/composables/usePusher";
import { debugQuotePostMedia } from "@/utils/quotePostMediaDebug";

// Lazy-load large modals so they don't impact startup time/memory.
const CreatePostModal = defineAsyncComponent(
  () => import("@/components/composer/CreatePostModal.vue"),
);
const CreateSongModal = defineAsyncComponent(
  () => import("@/components/composer/CreateSongModal.vue"),
);
const UpdateNotificationModal = defineAsyncComponent(
  () => import("@/components/UpdateNotificationModal.vue"),
);
const MobileDiscoverSheet = defineAsyncComponent(
  () => import("@/components/mobile/MobileDiscoverSheet.vue"),
);
const MobileFilterSheet = defineAsyncComponent(
  () => import("@/components/mobile/MobileFilterSheet.vue"),
);
const PrivacyPolicyModal = defineAsyncComponent(
  () => import("@/components/modals/PrivacyPolicyModal.vue"),
);
const TermsOfServiceModal = defineAsyncComponent(
  () => import("@/components/modals/TermsOfServiceModal.vue"),
);
const InviteFriendsModal = defineAsyncComponent(
  () => import("@/components/modals/InviteFriendsModal.vue"),
);
const GlobalRecordingButton = defineAsyncComponent(
  () => import("@/components/feedback/GlobalRecordingButton.vue"),
);
const CommentModal = defineAsyncComponent(
  () => import("@/components/comments/CommentModal.vue"),
);

const router = useRouter();
const route = useRoute();

const theme = useTheme();
const { mobile } = useDisplay();
const queryClient = useQueryClient();
const showQueryDevtools =
  (
    import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS as string | undefined
  )?.toLowerCase() === "true";
const { initialize, setDeveloperToken, isAuthenticated, user } = useAuth();
const appSettings = useAppSettings();
const uiStore = useUiStore();
const { chunkRecoveryToastVisible, searchShowCategoryTabs } =
  storeToRefs(uiStore);

/** Extra main top inset when search category tabs row is visible (after Enter / commit). */
const isSearchRouteWithSubheader = computed(() => {
  if (route.name !== "search") return false;
  // Must match when AppHeader shows the tab strip; do not gate on `?q=` or feed lags
  // `var(--header-height)` and the search reel will sit under the real header.
  return searchShowCategoryTabs.value;
});
const appModeStore = useAppModeStore();
const processingPosts = useProcessingPostsStore();

// Full-bleed vertical feed shell: overflow hidden on app-main; only the feed scrolls.
// Must NOT follow feedViewMode globally — that would lock scrolling on settings, messages, etc.
const isSinglePostFeedLayout = computed(() => {
  const isGroupFeed =
    route.name === "group-feed" || /^\/groups\/[^/]+\/feed$/.test(route.path);
  if (isGroupFeed) return true;

  if (route.name === "discover") return true;

  if (route.name === "profile" && uiStore.feedViewMode === "single-post") {
    return true;
  }

  const onTimelineShell =
    route.name === "timeline" || route.name === "discover-category";
  if (onTimelineShell && uiStore.feedViewMode === "single-post") {
    return true;
  }

  return false;
});

/** Post/burst permalink + single-post mode: same flex fill as timeline single-post feed */
const isPostDetailSinglePostMediaLayout = computed(() => {
  if (route.name !== "post-detail" && route.name !== "burst-detail")
    return false;
  return uiStore.feedViewMode === "single-post";
});

const useStackedViewportMain = computed(
  () => isSinglePostFeedLayout.value || isPostDetailSinglePostMediaLayout.value,
);

// Some mobile views own their viewport math and already reserve space for the
// fixed bottom chrome. Letting the shared shell reserve it again creates a
// second bottom gap on iOS.
const managesOwnBottomChrome = computed(() => {
  if (!mobile.value || hideBottomTabBar.value) return false;

  const routeName = route.name as string | undefined;

  return (
    routeName === "messages" ||
    routeName === "conversation" ||
    routeName === "groups" ||
    (routeName === "search" && uiStore.feedViewMode === "single-post")
  );
});

const reserveBottomTabSpaceInShell = computed(
  () =>
    mobile.value && !hideBottomTabBar.value && !managesOwnBottomChrome.value,
);

const themeColors = useThemeColors();
const { sdk } = useSdk();

// Invite Friends helpers (used in Settings modal)
const invite = useInviteFriends(user);

// Action feedback for app-level notifications
const { feedback, showFeedback } = useActionFeedback();

// Teleported modals may not inherit the Vuetify theme class from .v-application.
// Explicitly force theme when tenant UI mode is locked to a single value.
const settingsModalTheme = computed(() => {
  const mode = appSettings.uiMode.value;
  if (mode === "dark" || mode === "light") return mode;
  return theme.global.name.value;
});

// Hide AppHeader on some detail pages (mobile) to prevent conflicts with view-specific headers
// On desktop (>= 1280px), always show AppHeader even for detail pages
// Note: playlist-detail removed - always shows AppHeader for admin menu access
const isDetailPage = computed(() => {
  const detailRoutes = [
    // profile: uses AppHeader profile bar (Discover-style), same as desktop
    // post-detail / burst-detail: keep AppHeader + bottom chrome (standard toolbar) like other screens
    "song-detail",
    "video-detail",
    "group-detail",
  ];
  const isDetailRoute =
    route.name && detailRoutes.includes(route.name as string);
  // On desktop, always show AppHeader (return false to show it)
  // On mobile, hide AppHeader for detail pages (return true to hide it)
  return isDetailRoute && mobile.value;
});

// Routes where BottomTabBar should be hidden on mobile (full-bleed players / group shell).
const hideBottomTabBar = computed(() => {
  const routesWithoutBottomTab = ["song-detail", "group-detail"];
  const shouldHide =
    route.name && routesWithoutBottomTab.includes(route.name as string);
  return shouldHide && mobile.value;
});

// Routes that should use a wider center layout (no 750px cap; right column hidden via --wide)
const isWideLayout = computed(() => {
  const wideRoutes = ["playlists", "messages", "conversation", "gunloot"];
  return wideRoutes.includes(route.name as string);
});

/** Trending / widgets hidden on dedicated marketing surfaces (e.g. GunLoot). */
const showRightSidebar = computed(
  () => route.name !== "gunloot",
);

// Routes with fixed-position content that must respect left/right sidebars (group feed, groups list, discover, profile reel)
const hasFeedInsets = computed(() => {
  const name = route.name as string | undefined;
  if (!name) return false;
  if (name === "group-detail" || name === "group-manage") return false;
  if (name === "group-feed" || name.startsWith("groups")) return true;
  if (name === "discover" || name === "search") return true;
  return name === "profile" && uiStore.feedViewMode === "single-post";
});

// Initialize PWA features (badge API, install prompt, etc.)
const { setBadge } = usePWA();

// Update notification management
const { isUpdateAvailable, updateOptions, performUpdate, deferUpdate } =
  useUpdateNotification();

// Computed properties for update modal (only show in production, not dev)
const showUpdateModal = computed(
  () => isUpdateAvailable.value && !import.meta.env.DEV,
);
const updateTitle = computed(
  () => updateOptions.value.title || "Update Available",
);
const updateMessage = computed(
  () =>
    updateOptions.value.message ||
    "A new version of the app is ready to install.",
);
const updateFeatures = computed(() => updateOptions.value.features || []);

// Handle update actions
function handleAppUpdate() {
  performUpdate();
}

function handleAppUpdateLater() {
  deferUpdate();
}

// Provide app settings to child components
provide("appSettings", appSettings);

// Provide the main scroll container so virtualized lists can attach to it
const centerScrollRef = ref<HTMLElement | null>(null);
provide("appScrollContainer", centerScrollRef);

// When non-null, AppHeader follows this (driven by FeedList/VirtualFeed + useVirtualFeed).
// null = fall back to useScrollDirection (b725d2e). FeedList cannot provide() up to AppHeader (sibling layout).
const feedHeaderVisibilityOverride = ref<boolean | null>(null);
provide("feedHeaderVisibilityOverride", feedHeaderVisibilityOverride);

// App ready state - blocks DOM until settings load to prevent FOUC
const appReady = ref(false);

// App loading error state - shown when settings/branding fail to load
const appLoadError = ref<string | null>(null);
const isRetrying = ref(false);

// UI State
const drawer = ref(!mobile.value); // Start open on desktop, closed on mobile
const rail = ref(false);
const showSettings = ref(false);
const showSearch = ref(false);
const showMobileDiscover = ref(false);
const showMobileFilter = ref(false);
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);
const showInviteModal = ref(false);
const showGlobalRecordingButton = computed(() => {
  const state = screenRecordingService.state.value;
  return state === "countdown" || state === "recording";
});

// Mobile search input ref
const mobileSearchInputRef = ref<InstanceType<typeof SearchInputBase> | null>(
  null,
);

// Sync drawer state with screen size
watch(mobile, (isMobile) => {
  // Open drawer on desktop, close on mobile
  drawer.value = !isMobile;
});

// Keep drawer open in single-post mode on desktop
watch(isSinglePostFeedLayout, (isSinglePost) => {
  if (isSinglePost && !mobile.value) {
    drawer.value = true;
  }
});

// Close drawer when search opens (mobile only)
watch(showSearch, (isSearchOpen) => {
  if (isSearchOpen && mobile.value) {
    drawer.value = false;
  }
});

// Theme - derived from UI store
const currentTheme = computed(() => uiStore.effectiveTheme);

// Developer token
const developerToken = ref("");
const isSettingToken = ref(false);
const tokenError = ref("");
const settingsModalRef = ref<HTMLElement | null>(null);
const settingsCloseButtonRef = ref<HTMLButtonElement | null>(null);
const searchModalRef = ref<HTMLElement | null>(null);

// API base URL (read-only, from env)
import { resolveApiBaseUrl } from "@/utils/env";
const apiBaseUrl = resolveApiBaseUrl();

// Keep logs quiet in production/hot paths
const DEBUG_LOGS = import.meta.env.DEV;

async function handleCopyInvite() {
  const ok = await invite.copyInviteLink();
  if (ok) {
    showFeedback("mdi-check", "Link copied", "success");
  } else {
    showFeedback("mdi-alert-circle-outline", "Copy failed", "error");
  }
}

async function handleShareInvite() {
  const ok = await invite.shareInviteLink();
  if (ok) showFeedback("mdi-share", "Invite sent", "success");
}

function closeSettingsModal() {
  showSettings.value = false;
}

function closeSearchModal() {
  showSearch.value = false;
}

// Feed filter (provided to child components)
const feedFilter = ref<FeedFilter>("all");
provide("feedFilter", feedFilter);

// Method to open mobile filter sheet (provided to child components)
function openMobileFilterSheet() {
  showMobileFilter.value = true;
}
provide("openMobileFilterSheet", openMobileFilterSheet);

// Audio player ref
const audioPlayerRef = ref<InstanceType<typeof AudioPlayer> | null>(null);

// Use the audio player composable for reactive state
const {
  isPlaying: audioIsPlaying,
  currentTrack: audioCurrentTrack,
  setRepeatMode,
} = useAudioPlayer();

// Current track ID (provided to child components for highlighting)
// Returns the current track ID regardless of playing/paused state
const currentPlayingId = computed(() => {
  const track = audioCurrentTrack.value;
  return track?.ulid || track?.id || null;
});
provide("currentPlayingId", currentPlayingId);

// Audio playing state (provided to child components)
// Use the singleton ref directly for proper reactivity
provide("isAudioPlaying", audioIsPlaying);

// Check if audio player is visible (has a track loaded)
const hasAudioPlayer = computed(() => !!audioCurrentTrack.value);

// Play audio function (provided to child components)
function playAudio(post: Post, queue?: Post[]) {
  audioPlayerRef.value?.play(post, queue);
}
provide("playAudio", playAudio);

// Toggle audio playback (pause/resume)
function toggleAudio() {
  audioPlayerRef.value?.togglePlay();
}
provide("toggleAudio", toggleAudio);

// Set repeat mode (provided for featured playlists/radio stations)
provide("setRepeatMode", setRepeatMode);

// Video player ref
const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null);

// Play video function (provided to child components)
function playVideo(post: Post) {
  videoPlayerRef.value?.open(post);
}
provide("playVideo", playVideo);

// Comment modal ref
interface CommentModalHandle {
  open: (post: Post) => void;
}
const isCommentModalMounted = ref(false);
const commentModalRef = ref<CommentModalHandle | null>(null);
const pendingCommentModalPost = ref<Post | null>(null);

watch(commentModalRef, (newRef) => {
  if (newRef?.open && pendingCommentModalPost.value) {
    const post = pendingCommentModalPost.value;
    pendingCommentModalPost.value = null;
    newRef.open(post);
  }
});

// Open comments function (provided to child components)
// Anyone can view comments - auth check moved to submitComment in CommentModal
function openComments(post: Post) {
  if (commentModalRef.value?.open) {
    commentModalRef.value.open(post);
    return;
  }

  pendingCommentModalPost.value = post;
  if (!isCommentModalMounted.value) {
    isCommentModalMounted.value = true;
  }
}
provide("openComments", openComments);

// Create post modal ref
interface CreatePostModalHandle {
  open: (options?: OpenCreatePostOptions) => void;
  close: () => void;
  isOpen: Ref<boolean>;
}
const isCreatePostModalMounted = ref(false);
const createPostModalRef = ref<CreatePostModalHandle | null>(null);
const pendingOpenPostModal = ref(false);
const pendingOpenPostModalOptions = ref<OpenCreatePostOptions | undefined>(
  undefined,
);

// Watch for when the async component finishes mounting
watch(createPostModalRef, (newRef) => {
  if (newRef?.open && pendingOpenPostModal.value) {
    pendingOpenPostModal.value = false;
    const opts = pendingOpenPostModalOptions.value;
    pendingOpenPostModalOptions.value = undefined;
    newRef.open(opts);
    if (DEBUG_LOGS)
      console.log("[App] Post modal open() called after async mount");
  }
});

// Open create post modal (provided to child components). Pass options.groupName/groupAvatar to post to a group; commentApiParentUlid for stacked comment composer.
function openCreatePost(options?: OpenCreatePostOptions) {
  // Check if authenticated first
  if (!authService.isAuthenticated()) {
    router.push({
      name: "login",
      query: { redirect: router.currentRoute.value.fullPath },
    });
    return;
  }

  // If ref is already available, open directly
  if (createPostModalRef.value?.open) {
    createPostModalRef.value.open(options);
    if (DEBUG_LOGS) console.log("[App] Post modal open() called successfully");
    return;
  }

  // Mount on demand and set pending flag + options
  pendingOpenPostModalOptions.value = options;
  pendingOpenPostModal.value = true;
  if (!isCreatePostModalMounted.value) {
    isCreatePostModalMounted.value = true;
  }
}
provide("openCreatePost", openCreatePost);

// Create song modal ref
interface CreateSongModalHandle {
  open: () => void;
  close: () => void;
  isOpen: Ref<boolean>;
}
const isCreateSongModalMounted = ref(false);
const createSongModalRef = ref<CreateSongModalHandle | null>(null);
const pendingOpenSongModal = ref(false);

// Watch for when the async component finishes mounting
watch(createSongModalRef, (newRef) => {
  if (newRef?.open && pendingOpenSongModal.value) {
    pendingOpenSongModal.value = false;
    newRef.open();
    if (DEBUG_LOGS) console.log("[App] Modal open() called after async mount");
  }
});

// Open create song modal directly (provided to child components)
function openCreateSong() {
  // Check if authenticated first
  if (!authService.isAuthenticated()) {
    router.push({
      name: "login",
      query: { redirect: "/music/upload" },
    });
    return;
  }

  if (DEBUG_LOGS) {
    console.log("[App] Opening create song modal directly (no routing)");
  }

  // If ref is already available, open directly
  if (createSongModalRef.value?.open) {
    createSongModalRef.value.open();
    if (DEBUG_LOGS) console.log("[App] Modal open() called successfully");
    return;
  }

  // Mount on demand and set pending flag
  pendingOpenSongModal.value = true;
  if (!isCreateSongModalMounted.value) {
    isCreateSongModalMounted.value = true;
  }
}
provide("openCreateSong", openCreateSong);

function closeCreateSong() {
  if (DEBUG_LOGS)
    console.log("[App] Closing upload modal (no routing changes)");
  // Modal simply closes - no routing changes needed
}

function handleFilterChange(filter: FeedFilter) {
  feedFilter.value = filter;
}

// Handle mobile search — SearchInputBase navigates to /search, just close modal
function handleMobileSearch() {
  closeSearchModal();
}

// Handle developer token setting
async function handleSetDeveloperToken() {
  if (!developerToken.value.trim()) return;

  isSettingToken.value = true;
  tokenError.value = "";

  try {
    await setDeveloperToken(developerToken.value.trim());
    showFeedback("mdi-check", "Token set", "success");
    closeSettingsModal();
    developerToken.value = "";
  } catch (err) {
    tokenError.value =
      err instanceof Error ? err.message : "Failed to set token";
  } finally {
    isSettingToken.value = false;
  }
}

// Watch theme changes from UI store
watch(
  () => uiStore.effectiveTheme,
  (newTheme) => {
    console.log("[Theme] current:", newTheme);
    // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
    theme.change?.(newTheme);
  },
  { immediate: true },
);

useDialogA11y({
  isOpen: showSettings,
  containerRef: settingsModalRef,
  onClose: closeSettingsModal,
  focusInitial: () => {
    settingsCloseButtonRef.value?.focus();
  },
});

useDialogA11y({
  isOpen: showSearch,
  containerRef: searchModalRef,
  onClose: closeSearchModal,
  focusInitial: () => {
    mobileSearchInputRef.value?.focus();
  },
});

// Watch for uiMode changes from settings to force theme when locked
watch(
  () => appSettings.uiMode.value,
  (mode) => {
    if (mode === "light" || mode === "dark") {
      uiStore.setTheme(mode);
    }
  },
  { immediate: true },
);

// Handle real-time chat unread count updates
function handleChatUnreadUpdate(data: ChatUnreadCountEvent) {
  if (DEBUG_LOGS) console.log("[App] Received chat unread count update:", data);

  // Update the specific group's unread count in cache
  updateGroupInCache(queryClient, data.conversationUlid, {
    unreadCount: data.unreadCount,
  });
}

// Handle post processing complete events (background post creation completed)
function handlePostProcessingComplete(data: PostProcessingCompleteEvent) {
  if (DEBUG_LOGS)
    console.log(
      "[App] Post processing complete, invalidating all queries:",
      data,
    );

  debugQuotePostMedia("PostProcessingComplete (Pusher)", {
    ulid: data.ulid,
    type: data.type,
  });
  debugQuotePostMedia(
    "PostProcessingComplete → invalidateQueries: feed.all, posts.all, profile.all, users.all, trending.all, search.all (refetchType: all)",
  );

  // Fast-clear the SDK watcher (races against the next poll tick).
  processingPosts.markComplete(data.ulid);

  // The PostProcessingComplete event is broadcast AFTER InvalidatePostCacheJob
  // runs, but on slow CDN propagation the playable URL may not be live by the
  // time we refetch (#264). Poll the post until it actually carries a video URL
  // (or images) before broadcasting the cache invalidation — otherwise the
  // optimistic blob in the feed gets replaced with a post that has nothing.
  void (async () => {
    const isVideoPost = data.type === "video" || data.type === "VIDEO";
    const hasPlayableMedia = (p: Post | null | undefined): boolean => {
      if (!p) return false;
      const urls = (p as Post & { videoUrls?: { mp4?: string; hls?: string } })
        .videoUrls;
      if (urls?.mp4 || urls?.hls) return true;
      const images = (p as Post & { images?: unknown[] }).images;
      if (Array.isArray(images) && images.length > 0) return true;
      // Non-media post types (text, repost) don't need to wait.
      return !isVideoPost;
    };

    // Up to ~10s of polling (1s base, capped at 6 tries) so the optimistic
    // blob stays visible while the API catches up.
    let refreshed: Post | null = null;
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        refreshed = await sdk.getPostByUlid(data.ulid, true);
      } catch (err) {
        console.warn(
          "[App] PostProcessingComplete: SDK refresh failed for",
          data.ulid,
          err,
        );
      }
      if (hasPlayableMedia(refreshed)) break;
      await new Promise((resolve) => setTimeout(resolve, 1000 + attempt * 500));
    }

    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.feed.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.posts.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.profile.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.trending.all,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.search.all,
          refetchType: "all",
        }),
      ]);
    } catch (err) {
      console.error(
        "[App] Error invalidating queries after post processing:",
        err,
      );
    }
  })();
}

// Vue app context, captured synchronously during setup. Needed to restore the
// injection context for deferred realtime bootstrap: usePusher() -> useAuth()
// calls useRouter()/useQueryClient(), which require an active injection context.
// After the dynamic import()/setTimeout below, that context is gone unless we
// re-enter it via app.runWithContext().
const realtimeAppContext = getCurrentInstance()?.appContext.app ?? null;

// Track current user ULID for channel subscription
let currentUserUlid: string | null = null;
let realtimeStartTimeout: ReturnType<typeof window.setTimeout> | null = null;
const realtimeClient = shallowRef<{
  subscribeToUserChannel: (
    userUlid: string,
    handler: (data: ChatUnreadCountEvent) => void,
  ) => void;
  subscribeToPostProcessingComplete: (
    userUlid: string,
    handler: (data: PostProcessingCompleteEvent) => void,
  ) => void;
  unsubscribeFromUserChannel: (userUlid: string) => void;
  disconnect: () => void;
} | null>(null);
let realtimeClientPromise: Promise<
  NonNullable<typeof realtimeClient.value>
> | null = null;

async function getRealtimeClient() {
  if (realtimeClient.value) {
    return realtimeClient.value;
  }

  if (!realtimeClientPromise) {
    realtimeClientPromise = import("@/composables/usePusher").then(
      ({ usePusher }) => {
        // usePusher() -> useAuth() uses vue-router/vue-query injection, which
        // needs an active injection context. The dynamic import resolves in a
        // microtask with no component on the stack, so re-enter the app context.
        const client = realtimeAppContext
          ? realtimeAppContext.runWithContext(() => usePusher())
          : usePusher();
        realtimeClient.value = client;
        return client;
      },
    );
  }

  return realtimeClientPromise;
}

function clearRealtimeBootstrap() {
  if (realtimeStartTimeout) {
    clearTimeout(realtimeStartTimeout);
    realtimeStartTimeout = null;
  }
}

async function ensureRealtimeSubscriptions(userUlid: string) {
  const client = await getRealtimeClient();

  if (!isAuthenticated.value || user.value?.ulid !== userUlid) {
    return;
  }

  if (currentUserUlid === userUlid) {
    return;
  }

  if (currentUserUlid) {
    client.unsubscribeFromUserChannel(currentUserUlid);
  }

  currentUserUlid = userUlid;
  client.subscribeToUserChannel(userUlid, handleChatUnreadUpdate);
  client.subscribeToPostProcessingComplete(
    userUlid,
    handlePostProcessingComplete,
  );
}

function scheduleRealtimeBootstrap() {
  const userUlid = user.value?.ulid;
  if (!isAuthenticated.value || !userUlid) {
    return;
  }

  if (realtimeClient.value) {
    void ensureRealtimeSubscriptions(userUlid);
    return;
  }

  if (realtimeClientPromise || realtimeStartTimeout) {
    return;
  }

  realtimeStartTimeout = window.setTimeout(() => {
    realtimeStartTimeout = null;
    void ensureRealtimeSubscriptions(userUlid);
  }, 2500);
}

function teardownRealtime() {
  clearRealtimeBootstrap();

  if (currentUserUlid && realtimeClient.value) {
    realtimeClient.value.unsubscribeFromUserChannel(currentUserUlid);
  }

  currentUserUlid = null;
  realtimeClient.value?.disconnect();
}

// Watch auth state to connect/disconnect WebSocket notifications and user channel
watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      scheduleRealtimeBootstrap();
    } else {
      teardownRealtime();
    }
  },
  { immediate: true },
);

// Also watch for user data changes (in case user loads after auth)
watch(user, (newUser) => {
  if (
    isAuthenticated.value &&
    newUser?.ulid &&
    newUser.ulid !== currentUserUlid
  ) {
    scheduleRealtimeBootstrap();
  }
});

// Main content ref
const appMainRef = ref<HTMLElement | null>(null);

// Cleanup function refs (set in onMounted, called in onUnmounted)
const cleanupNetworkRef = ref<(() => void) | null>(null);
const cleanupDeepLinkingRef = ref<(() => void) | null>(null);
const cleanupBrowserChromeRef = ref<(() => void) | null>(null);
const cleanupIosPwaLayoutRef = ref<(() => void) | null>(null);

// Initialize composables at top level (required for inject() to work)
const networkStatus = useNetworkStatus();
const deepLinking = useDeepLinking();

// Global popstate handler - navigates to home when user lands directly on a page
// and presses back button (no history to go back to)
function handleGlobalPopState() {
  // Check if player or signup modal is handling this popstate
  if (
    (window as any).__playerHandlingPopstate ||
    (window as any).__signupModalHandlingPopstate
  ) {
    return;
  }

  // Check if we're now at a "first page" state after the popstate
  // This happens when user landed directly on a deep link and pressed back
  if (isFirstPage()) {
    // Navigate to home instead of leaving the app
    // Use replace to avoid creating new history entry
    router.replace("/");
  }
}

/**
 * Load essential app configuration (settings, theme, auth)
 * This must succeed for the app to function
 */
async function loadAppConfiguration(): Promise<void> {
  // Load app settings (reactions, genres, badges, UI settings)
  await appSettings.loadSettings();

  // Check if settings loaded successfully - if error occurred, throw
  if (appSettings.error.value) {
    throw new Error(appSettings.error.value);
  }

  // Check if we got essential settings data
  // siteType being empty could indicate settings didn't load properly
  // but we don't require it - some tenants may not have it configured

  // Update document title with site name from settings
  const siteName = appSettings.uiSettings.value?.name;
  if (siteName) {
    document.title = siteName;
    // Also update Apple meta tag for PWA
    const appleTitleEl = document.getElementById("apple-title");
    if (appleTitleEl) {
      appleTitleEl.setAttribute("content", siteName);
    }
  }

  // Apply theme colors from API settings to Vuetify
  // Call directly with the loaded settings
  if (
    appSettings.uiSettings.value &&
    Object.keys(appSettings.uiSettings.value).length > 0
  ) {
    if (DEBUG_LOGS)
      console.log("[App] Applying theme colors directly after settings load");
    await themeColors.applyAllColors(appSettings.uiSettings.value);
  }
  // Set up watcher for future settings changes (hot reload, real-time updates)
  // Skip initializeThemeColors() — it would redundantly re-apply colors we just applied above
  watch(
    () => appSettings.uiSettings.value,
    (newSettings) => {
      if (newSettings && Object.keys(newSettings).length > 0) {
        themeColors.applyAllColors(newSettings);
      }
    },
    { deep: true },
  );

  // Initialize Google Analytics with tenant's Measurement ID (if configured)
  const gaId = (appSettings.uiSettings.value as Record<string, unknown>)
    ?.googleAnalyticsId as string | undefined;
  if (gaId) {
    initAnalytics(gaId);
  }

  // Re-initialize feed view mode after settings load (settings may contain feed view mode config)
  // The useAppSettings composable will call setFeedViewModeConfig, but we also call initializeFeedViewMode
  // to ensure it respects the config and applies saved user preference or default
  uiStore.initializeFeedViewMode();

  // Initialize auth BEFORE showing app to ensure badges are loaded
  await initialize();
}

/**
 * Retry loading app configuration after an error
 */
async function retryAppLoad() {
  isRetrying.value = true;
  appLoadError.value = null;

  try {
    // Invalidate cache to force fresh fetch
    appSettings.invalidateCache();

    await loadAppConfiguration();

    // Success - show the app
    appReady.value = true;
    await nextTick();
    nudgeIosStandaloneViewportLayout();
  } catch (error) {
    console.error("[App] Retry failed:", error);
    appLoadError.value =
      error instanceof Error
        ? error.message
        : "Failed to load app configuration. Please check your internet connection and try again.";
  } finally {
    isRetrying.value = false;
  }
}

/** iOS home-screen PWA: WebKit often reports wrong dvh/safe-area for a few frames on cold open. */
function isIosStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true
  );
}

function readIosLayoutGeometry(): void {
  void document.documentElement.getBoundingClientRect().height;
  void window.visualViewport?.height;
}

/**
 * iOS PWA cold relaunch (force-quit → home-screen icon): WebKit reports the viewport COLLAPSED —
 * window.innerHeight / 100vh / 100dvh all read screen.height minus the top inset (verified on
 * device: 644 instead of 693). The fixed BottomTabBar then lands at bottom:644 and a ~49px band
 * shows below it. A synthetic `resize` does NOT make WebKit re-expand the viewport; only a real
 * viewport-config change (or device rotation) does — which is why navigating to Search / opening a
 * popover "fixes" the gap. We reproduce that programmatically by briefly toggling the viewport meta
 * `viewport-fit=cover → auto → cover`, forcing WebKit to re-resolve the viewport so it self-heals.
 *
 * Each toggle relayouts (a visible flash) so fire it only when the viewport actually looks
 * collapsed, and cap total toggles per relaunch.
 */
const IOS_VIEWPORT_RECALC_MAX = 2;
let iosViewportRecalcCount = 0;

function isIosViewportCollapsed(): boolean {
  // screen.height is the physical screen; innerHeight should converge to it (portrait PWA, no
  // browser chrome). A meaningful shortfall = WebKit hasn't expanded the viewport yet.
  const inner = window.innerHeight;
  const screenH = window.screen.height;
  return screenH > 0 && inner > 0 && screenH - inner > 24;
}

function forceIosViewportRecalc(): void {
  if (iosViewportRecalcCount >= IOS_VIEWPORT_RECALC_MAX) return;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  const original = meta?.getAttribute("content");
  if (!meta || !original || !original.includes("viewport-fit=cover")) return;
  iosViewportRecalcCount += 1;
  meta.setAttribute(
    "content",
    original.replace("viewport-fit=cover", "viewport-fit=auto"),
  );
  requestAnimationFrame(() => {
    meta.setAttribute("content", original);
  });
}

/** Cleared before each schedule so pageshow + appReady do not stack duplicate kicks. */
const iosPwaLayoutNudgeTimeoutIds: number[] = [];

function clearIosPwaLayoutNudgeTimeouts(): void {
  for (const id of iosPwaLayoutNudgeTimeoutIds) {
    window.clearTimeout(id);
  }
  iosPwaLayoutNudgeTimeoutIds.length = 0;
}

function nudgeIosStandaloneViewportLayout(): void {
  if (!isIosStandalonePwa()) return;
  const kick = (): void => {
    readIosLayoutGeometry();
    // Cold-relaunch viewport collapse — re-expand it (bounded, flashes once) before the resize.
    if (isIosViewportCollapsed()) {
      forceIosViewportRecalc();
    }
    window.dispatchEvent(new Event("resize"));
  };
  clearIosPwaLayoutNudgeTimeouts();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      kick();
      // WebKit can settle dvh/safe-area well after first paint (PR #291); extend past loading fade.
      for (const delayMs of [32, 120, 400, 800, 1500]) {
        iosPwaLayoutNudgeTimeoutIds.push(window.setTimeout(kick, delayMs));
      }
    });
  });
}

function handleIosStandalonePageShow(event: PageTransitionEvent): void {
  // bfcache restore (force-quit → reopen) is exactly when the viewport collapses again — reset
  // the toggle budget so this fresh relaunch can re-expand the viewport.
  if (event.persisted) {
    iosViewportRecalcCount = 0;
  }
  nudgeIosStandaloneViewportLayout();
}

const debouncedNudgeIosStandaloneViewportLayout = debounce(
  nudgeIosStandaloneViewportLayout,
  80,
);

watch(appReady, (ready) => {
  if (!ready) return;
  void nextTick(() => {
    nudgeIosStandaloneViewportLayout();
  });
});

// Initialize on mount
onMounted(async () => {
  // Initialize UI store (theme, sidebar state)
  uiStore.initializeTheme();
  uiStore.initializeSidebar();
  uiStore.initializeFeedViewMode();

  if (typeof window !== "undefined") {
    window.addEventListener("pageshow", handleIosStandalonePageShow);
    window.visualViewport?.addEventListener(
      "resize",
      debouncedNudgeIosStandaloneViewportLayout,
    );
    cleanupIosPwaLayoutRef.value = () => {
      window.removeEventListener("pageshow", handleIosStandalonePageShow);
      window.visualViewport?.removeEventListener(
        "resize",
        debouncedNudgeIosStandaloneViewportLayout,
      );
      debouncedNudgeIosStandaloneViewportLayout.cancel();
      clearIosPwaLayoutNudgeTimeouts();
    };
  }

  // Ensure drawer is open in single-post mode on desktop
  if (isSinglePostFeedLayout.value && !mobile.value) {
    drawer.value = true;
  }

  // Apply saved theme to Vuetify
  // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
  theme.change?.(uiStore.effectiveTheme);

  // Load essential app configuration with error handling
  try {
    await loadAppConfiguration();

    // App is ready - settings loaded, theme colors applied, and auth initialized
    appReady.value = true;
  } catch (error) {
    console.error("[App] Failed to load app configuration:", error);
    appLoadError.value =
      error instanceof Error
        ? error.message
        : "Failed to load app configuration. Please check your internet connection and try again.";
    // Don't set appReady - keep showing loading/error screen
    return;
  }

  // Initialize network status monitoring (composable initialized at top level)
  cleanupNetworkRef.value = networkStatus.initialize();

  // Initialize deep linking (native platforms only, composable initialized at top level)
  cleanupDeepLinkingRef.value = deepLinking.initialize();

  // Mobile browser chrome detection and compensation (iOS Safari & Android Chrome)
  // Calculate browser chrome height (difference between 100vh and 100dvh)
  // and set CSS variable for positioning components above the bottom tab bar
  if (typeof window !== "undefined") {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    // iOS “Add to Home Screen” + manifest display modes: no URL bar; skip chrome compensation.
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone ===
        true;

    // Apply browser chrome compensation on mobile browsers (iOS Safari & Android Chrome)
    // but not in standalone/PWA mode where browser chrome doesn't exist
    if ((isIOS || isAndroid) && !isStandalone && window.visualViewport) {
      function updateBrowserChromeHeight() {
        const vh = window.innerHeight;
        const dvh = window.visualViewport?.height || window.innerHeight;
        const chromeHeight = Math.max(0, vh - dvh);
        document.documentElement.style.setProperty(
          "--mobile-browser-chrome-height",
          `${chromeHeight}px`,
        );
      }

      // Update on mount and when viewport changes (browser chrome visibility)
      updateBrowserChromeHeight();
      window.visualViewport?.addEventListener(
        "resize",
        updateBrowserChromeHeight,
      );
      window.addEventListener("resize", updateBrowserChromeHeight);

      // Store cleanup function (called in top-level onUnmounted)
      cleanupBrowserChromeRef.value = () => {
        window.visualViewport?.removeEventListener(
          "resize",
          updateBrowserChromeHeight,
        );
        window.removeEventListener("resize", updateBrowserChromeHeight);
        document.documentElement.style.removeProperty(
          "--mobile-browser-chrome-height",
        );
      };
    }
  }

  // Listen for push notification clicks from service worker (deep linking)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);
  }

  // Global popstate handler for browser back button
  // Navigates to home when there's no history to go back to (first page scenario)
  window.addEventListener("popstate", handleGlobalPopState);

  await nextTick();
  nudgeIosStandaloneViewportLayout();
});

function onServiceWorkerMessage(event: MessageEvent) {
  if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.url) {
    if (DEBUG_LOGS) {
      console.log("[App] Notification click - navigating to:", event.data.url);
    }
    router.push(event.data.url);
  }
}

onUnmounted(() => {
  // Clean up global popstate listener
  window.removeEventListener("popstate", handleGlobalPopState);

  // Clean up service worker message listener (helps dev/HMR and test environments)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.removeEventListener(
      "message",
      onServiceWorkerMessage,
    );
  }

  // Clean up network status monitoring
  if (cleanupNetworkRef.value) {
    cleanupNetworkRef.value();
  }

  // Clean up deep linking
  if (cleanupDeepLinkingRef.value) {
    cleanupDeepLinkingRef.value();
  }

  // Clean up browser chrome height monitoring
  if (cleanupBrowserChromeRef.value) {
    cleanupBrowserChromeRef.value();
  }

  if (cleanupIosPwaLayoutRef.value) {
    cleanupIosPwaLayoutRef.value();
  }
});
</script>

<style lang="scss">
// Define layout constants as CSS custom properties on the app root
// These are used by both header and main content for alignment
.v-application {
  --sidebar-width: 260px;
  --sidebar-collapsed-width: 72px;
  --right-sidebar-width: 350px;
  --content-max-width: 1100px; // center (750px) + right sidebar (350px)
  // Total app width = sidebar + content max-width = 260 + 1100 = 1360px
  --app-max-width: calc(var(--sidebar-width) + var(--content-max-width));
  --app-center-span: calc(var(--content-max-width) - var(--right-sidebar-width));
  --app-header-offset-left: 0px;
  --app-header-offset-right: 0px;

  // Desktop (lg+): header spans center column only; sidebars run full height from top.
  @media (min-width: 1280px) {
    --app-shell-gutter: 0px;
    --app-center-span: calc(var(--content-max-width) - var(--right-sidebar-width));

    &[data-wide-layout="true"] {
      --app-center-span: var(--content-max-width);
    }

    &[data-has-right-rail="true"] {
      --app-header-offset-right: var(--right-sidebar-width);
    }

    &[data-has-right-rail="false"] {
      --app-header-offset-right: 0px;
    }

    &[data-drawer-open="true"] {
      --app-header-offset-left: var(--sidebar-width);
    }

    &[data-drawer-open="false"] {
      --app-header-offset-left: var(--sidebar-collapsed-width);
    }

    @media (min-width: 1360px) {
      --app-shell-gutter: calc((100vw - var(--app-max-width)) / 2);

      &[data-drawer-open="true"] {
        --app-header-offset-left: calc(
          var(--app-shell-gutter) + var(--sidebar-width)
        );
      }

      &[data-drawer-open="false"] {
        --app-header-offset-left: calc(
          var(--app-shell-gutter) + var(--sidebar-collapsed-width)
        );
      }

      &[data-has-right-rail="true"] {
        --app-header-offset-right: calc(
          var(--app-shell-gutter) + var(--right-sidebar-width)
        );
      }

      &[data-has-right-rail="false"] {
        --app-header-offset-right: var(--app-shell-gutter);
      }
    }

    // Fixed full-viewport layers (communities reel, discover, etc.) share header bounds.
    --feed-inset-left: var(--app-header-offset-left);
    --feed-inset-right: var(--app-header-offset-right);
  }
}

// App wrapper - wraps app-container and bottom bar for single-post mode stacking
.app-wrapper {
  // Single-post mode: use flexbox to stack header/content and bottom bar
  &--single-post {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }
}

// App container - wraps header, sidebar, and main content
// Centers everything on wide screens
.app-container {
  position: relative;
  width: 100%;
  max-width: var(--app-max-width);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100dvh;

  // Match fixed header/sidebar gutter math (margin: auto can differ by subpixel from calc).
  @media (min-width: 1360px) {
    width: var(--app-max-width);
    margin-left: var(--app-shell-gutter, 0px);
    margin-right: var(--app-shell-gutter, 0px);
  }

  // Single-post mode: fill available space
  .app-wrapper--single-post & {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  // When drawer is open, the container constrains all elements
  &--drawer-open {
    // Header, sidebar, and main all live within this container
    // Their positions are relative to this container now
  }
}

// App main content area
// margin-top accounts for fixed header height + safe area inset (notch/Dynamic Island)
// NO padding-top needed since margin handles the offset
.app-main {
  // Define header height as CSS variable for consistency
  // Desktop: 64px, Mobile: 56px (smaller header)
  --header-height: calc(64px + env(safe-area-inset-top, 0px));
  --header-height-mobile: calc(56px + env(safe-area-inset-top, 0px));

  padding-top: 0;
  padding-left: 0;
  // Let content dictate height, but ensure we at least fill the viewport minus the header
  min-height: calc(100vh - var(--header-height));
  min-height: calc(
    100dvh - var(--header-height)
  ); // Dynamic viewport height - accounts for iOS Safari toolbar
  height: auto;
  margin-top: var(--header-height); // Account for header + notch/Dynamic Island
  overflow: visible;
  background: transparent;
  transition:
    padding-left 0.3s var(--ease-out-expo),
    margin-top 0.3s var(--ease-out-expo);

  // When drawer is open on desktop, add left padding
  &--drawer-open {
    padding-left: var(--sidebar-width);
  }

  // When drawer is collapsed on desktop, add smaller left padding for icon sidebar
  &--drawer-collapsed {
    padding-left: var(--sidebar-collapsed-width);
  }

  // When bottom tab bar is visible (mobile), reduce height to account for it
  // Also use smaller mobile header height
  &--has-bottom-tab {
    --header-height: var(--header-height-mobile);
    min-height: calc(100vh - var(--header-height-mobile));
    min-height: calc(100dvh - var(--header-height-mobile));
    height: auto;
    margin-top: var(--header-height-mobile);
  }

  // Single-post mode: stacked layout (header -> content -> bottom bar)
  &--single-post {
    margin-top: var(--header-height) !important;
    padding-bottom: 0 !important;
    // Fill available space (bottom bar is now in normal flow)
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    // Desktop: keep sidebar visible in flex layout (insets come from .v-application)
    @media (min-width: 1280px) {
      padding-left: var(--sidebar-width) !important;
    }

    &.app-main--drawer-collapsed {
      @media (min-width: 1280px) {
        padding-left: var(--sidebar-collapsed-width) !important;
      }
    }

    // Wide layout (no right sidebar): feed can extend to right edge
    &:has(.app-layout--wide) {
      --feed-inset-right: 0px;
    }

    // Mobile: use mobile header height, no sidebar padding
    @media (max-width: 768px) {
      --header-height: var(--header-height-mobile);
      margin-top: var(--header-height-mobile) !important;
      padding-left: 0 !important;
      --feed-inset-left: 0px;
      --feed-inset-right: 0px;
    }

    @media (max-width: 1279px) {
      --feed-inset-left: 0px;
      --feed-inset-right: 0px;
    }

    // Row layout so right sidebar (Trending Now) stays on the right, not below
    .app-layout {
      height: 100%;
      min-height: 0;
      flex: 1;
      display: flex;
      flex-direction: row;
      overflow: hidden;

      &__center {
        height: 100%;
        min-height: 0;
        min-width: 0;
        max-width: 750px;
        overflow: hidden;
        padding: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      &__right {
        height: 100%;
        overflow: auto;
        align-self: stretch;
      }
    }
  }

  // Communities / discover fixed layers: --feed-inset-* inherited from .v-application (matches header).
  &--has-feed-insets {
    @media (max-width: 1279px) {
      --feed-inset-left: 0px;
      --feed-inset-right: 0px;
    }
  }

  /**
   * Search: two-row header (back+search+actions, then full-width tabs). This value
   * drives `margin-top` on main and `var(--header-height)` for the fixed search reel.
   * If it’s larger than the real `app-header` height, you get a band of empty space
   * under the header; if smaller, content tucks under the tab row.
   */
  &--search-subheader {
    /* Second row: full-width tab nav (see AppHeader `__search-page-tabs` on its own row) */
    --search-header-tab-strip: 22px;
    /* Top row is not always exactly 56/64px with padding; small trim vs over-reserving 20px */
    --search-header-stack-gap: 8px;
    --header-height: calc(
      64px + env(safe-area-inset-top, 0px) + var(--search-header-tab-strip) +
        var(--search-header-stack-gap)
    );
    --header-height-mobile: calc(
      56px + env(safe-area-inset-top, 0px) + var(--search-header-tab-strip) +
        var(--search-header-stack-gap)
    );
  }

  &--search-subheader.app-main--has-bottom-tab {
    --header-height: var(--header-height-mobile);
    margin-top: var(--header-height-mobile);
  }

  // iOS PWA / standalone mode - ensure proper safe area handling
  @supports (padding-top: env(safe-area-inset-top)) {
    @media (display-mode: standalone) {
      &:not(.app-main--search-subheader) {
        --header-height: calc(64px + env(safe-area-inset-top, 47px));
      }

      &.app-main--search-subheader {
        --header-height: calc(
          64px + env(safe-area-inset-top, 47px) +
            var(--search-header-tab-strip) + var(--search-header-stack-gap)
        );
        --header-height-mobile: calc(
          56px + env(safe-area-inset-top, 47px) +
            var(--search-header-tab-strip) + var(--search-header-stack-gap)
        );
      }
    }
  }
}

// Three-column layout
.app-layout {
  display: flex;
  // Max width is now handled by .app-container wrapper
  // No need for max-width or margin: 0 auto here
  height: auto;
  min-height: 100%;

  &__center {
    flex: 1;
    min-width: 0;
    max-width: 750px;
    height: auto;
    min-height: 100%;
    overflow-y: visible;
    overscroll-behavior-y: contain; // Prevent page refresh on PTR
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
    border-left: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    background: rgba(var(--v-theme-background), 0.3);
    padding-bottom: var(
      --app-center-bottom-inset,
      env(safe-area-inset-bottom, 0px)
    );

    // Search: column must not grow past the viewport; inner flex (posts / people) needs a
    // definite height budget so SinglePostFeedView + VerticalUserSlideScroller can scroll.
    &:has(.search-view) {
      display: flex;
      flex-direction: column;
      min-height: 0;
      max-height: calc(100dvh - var(--header-height));
    }
  }

  &__right {
    width: 350px;
    flex-shrink: 0;
    position: sticky;
    top: var(--header-height);
    align-self: flex-start;

    @media (min-width: 1280px) {
      top: 0;
    }
  }

  // Wide layout mode (for playlists, etc.)
  &--wide {
    max-width: 100%;

    .app-layout__center {
      max-width: 100%;
      border-right: none;
    }
  }

  // Add padding when audio player is visible
  // Player height varies by breakpoint:
  // - Desktop (>1700px): 88px fixed
  // - Mid-desktop (<=1700px): 56px compact single-row
  // - Mobile (<=768px): ~100px mini player + bottom tab bar
  // On iOS, also account for safe-area-inset-bottom (home indicator area)
  &--has-player {
    .app-layout__center {
      // Desktop: 88px player + 8px buffer
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    }
  }
}

// Mid-desktop player padding (compact single-row layout)
@media (max-width: 1700px) {
  .app-layout--has-player {
    .app-layout__center {
      // Compact player is 56px, add 8px buffer
      padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
    }
  }
}

// App loading screen fade-out transition
.app-loading-fade-leave-active {
  transition: opacity 0.3s var(--ease-out-expo);
}

.app-loading-fade-leave-to {
  opacity: 0;
}

// Page transition — keep short to avoid blocking iOS navigation feel.
// out-in mode ensures old page unmounts before new page mounts, preventing
// two heavy component trees from being alive simultaneously.
.page-fade-enter-active {
  transition: opacity 0.12s var(--ease-out-expo);
}

.page-fade-leave-active {
  transition: opacity 0.08s ease-out;
}

// Fixed full-viewport routes can sit above the next page during leave; don't block interaction.
.page-fade-leave-active,
.page-fade-leave-to {
  pointer-events: none;
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

// Modal overlay
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-overlay), 0.7);
  backdrop-filter: blur(4px);
  padding: 24px;

  &--fullscreen {
    padding: 0;
    align-items: flex-start;
  }
}

.modal-container {
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px); // Dynamic viewport height for iOS Safari
  overflow-y: auto;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 20px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
}

// Settings modal
.settings-modal {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 50%;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__content {
    padding: 24px;
  }
}

// Settings sections
.settings-section {
  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0 0 4px;
  }

  &__desc {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin: 0 0 16px;
  }
}

.settings-divider {
  height: 1px;
  margin: 24px 0;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.settings-theme-toggle {
  display: flex;
  gap: 8px;
}

.settings-theme-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  background: rgba(var(--v-theme-on-surface), 0.02);
  border-radius: 12px;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-color: rgba(var(--v-theme-on-surface), 0.15);
    color: rgb(var(--v-theme-on-surface));
  }

  &--active {
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.15),
      rgba(var(--v-theme-primary), 0.05)
    );
    border-color: rgba(var(--v-theme-primary), 0.3);
    color: rgb(var(--v-theme-primary));
  }
}

.settings-token {
  display: flex;
  gap: 8px;

  &__input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    outline: none;
    transition: all 0.2s ease;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.35);
    }

    &:focus {
      border-color: rgba(var(--v-theme-primary), 0.4);
      box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__btn {
    padding: 12px 20px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary));
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.settings-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 14px;
  background: rgba(var(--v-theme-error), 0.1);
  border: 1px solid rgba(var(--v-theme-error), 0.2);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: rgb(var(--v-theme-error));
}

.settings-api-url {
  padding: 12px 16px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 10px;

  code {
    font-family: "SF Mono", "Consolas", monospace;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

// Search modal
.search-modal {
  width: 100%;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    background: rgba(var(--v-theme-surface), 0.8);
    backdrop-filter: blur(10px);
  }

  &__back,
  &__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.5);
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__input {
    flex: 1;
    padding: 10px 0;
    border: none;
    background: none;
    font-family: var(--font-body);
    font-size: 1rem;
    color: rgb(var(--v-theme-on-surface));
    outline: none;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.4);
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px 0;
    overflow-y: auto;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 48px 24px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }

  &__suggestions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 12px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
    }

    .v-icon {
      color: rgba(var(--v-theme-on-surface), 0.5);
      flex-shrink: 0;
    }
  }

  &__suggestion-content {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-text {
    display: block;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-subtext {
    display: block;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
  }

  &__hint {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
    text-align: center;
    padding: 48px 24px;
  }
}

// Modal transition
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s var(--ease-out-expo);

  .modal-container,
  .search-modal {
    transition: all 0.3s var(--ease-out-expo);
  }
}

.modal-enter-from,
.modal-leave-to {
  background: rgba(0, 0, 0, 0);

  .modal-container {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }

  .search-modal {
    opacity: 0;
    transform: translateY(-20px);
  }
}

// Light theme overrides
.v-theme--light {
  .app-layout {
    &__center {
      border-color: rgba(0, 0, 0, 0.06);
      background: rgba(255, 255, 255, 0.4);
    }
  }

  .modal-overlay {
    background: rgba(var(--v-theme-overlay), 0.4);
  }

  // Modal container uses CSS variables - no light theme override needed
}

// Responsive adjustments
@media (max-width: 1280px) {
  .app-layout {
    max-width: 100%;

    &__center {
      border-right: none;
    }
  }
}

@media (max-width: 960px) {
  .app-layout {
    &__center {
      border-left: none;
    }
  }

  // Keep the shell full height on mobile and reserve space for the fixed tab bar
  // as content padding instead of shrinking the main viewport.
  // Safari reports env(safe-area-inset-bottom) correctly from first paint, so use the raw env()
  // value to avoid double-padding (the home indicator falls in the safe area, not under chrome).
  .app-main--has-bottom-tab:not(.app-main--single-post) {
    --app-center-bottom-inset: calc(56px + env(safe-area-inset-bottom, 0px));
  }

  .app-main--manages-bottom-chrome:not(.app-main--single-post) {
    --app-center-bottom-inset: 0px;
  }

  // Installed PWA only — WebKit can report env(safe-area-inset-bottom) as 0 on cold launch,
  // so fall back to the unified --app-bottom-tab-clearance. Scoped to standalone so Safari is unaffected.
  @media (display-mode: standalone), (display-mode: fullscreen),
    (display-mode: minimal-ui) {
    .app-main--has-bottom-tab:not(.app-main--single-post) {
      --app-center-bottom-inset: var(--app-bottom-tab-clearance);
    }
  }

  // Routes that set managesOwnBottomChrome (search single-post reel, messages, …) skip
  // app-main--has-bottom-tab, so --header-height never swaps to 56px + safe — but
  // AppHeader still uses app-header--mobile below md. Align main margin + inherited vars.
  .app-main--manages-bottom-chrome {
    --header-height: var(--header-height-mobile);
    margin-top: var(--header-height-mobile);
  }
}

// Mobile-specific player padding
// Mini player sits above the bottom tab bar, so padding = tab bar (56px) + mini player (~100px)
// On mobile browsers (iOS Safari & Android Chrome), also account for browser chrome height
@media (max-width: 768px) {
  .app-layout--has-player {
    .app-layout__center {
      // Bottom tab bar (56px) + mini player (~100px) + small buffer
      // Safe area is already handled by the bottom tab bar position
      // Browser chrome height is added to ensure content isn't hidden behind chrome
      padding-bottom: calc(
        56px + 108px + env(safe-area-inset-bottom, 0px) +
          var(--mobile-browser-chrome-height, 0px)
      );
    }
  }
}

// Portrait-only overlay for mobile landscape
.landscape-overlay {
  display: none;
  position: fixed;
  inset: 0;
  z-index: var(--z-loading-overlay);
  background: rgb(var(--v-theme-surface));
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  color: rgb(var(--v-theme-on-surface));
  text-align: center;
  padding: 24px;

  &__icon {
    font-size: 64px;
    animation: rotate-phone 2s ease-in-out infinite;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }

  &__message {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin: 0;
  }
}

@keyframes rotate-phone {
  0%,
  100% {
    transform: rotate(0deg);
  }

  25% {
    transform: rotate(-15deg);
  }

  75% {
    transform: rotate(15deg);
  }
}

// App loading screen - skeleton matches the app shell to prevent CLS on mount.
// The header, body, and bottom-bar reserve the same space as the real components
// so that when appReady flips, content slides in without shifting.
.app-loading {
  position: fixed;
  inset: 0;
  z-index: var(--z-loading-overlay);
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-background));

  // Skeleton header — matches AppHeader (56px + safe-area-top)
  &__skeleton-header {
    height: calc(56px + env(safe-area-inset-top, 0px));
    flex-shrink: 0;
    background: rgba(var(--v-theme-on-background), 0.03);
    border-bottom: 1px solid rgba(var(--v-theme-on-background), 0.06);
  }

  // Skeleton body — fills remaining space, centers spinner
  &__skeleton-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Skeleton bottom bar — matches BottomTabBar (--safe-area-bottom / standalone fallbacks)
  &__skeleton-bottom-bar {
    height: var(--app-bottom-tab-clearance);
    flex-shrink: 0;
    background: rgba(var(--v-theme-on-background), 0.03);
    border-top: 1px solid rgba(var(--v-theme-on-background), 0.06);
  }

  &__spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(var(--v-theme-on-background), 0.1);
    border-top-color: rgb(var(--v-theme-primary));
    border-radius: 50%;
    animation: app-loading-spin 0.8s linear infinite;
  }

  // Error state
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
    max-width: 400px;
  }

  &__error-icon {
    opacity: 0.8;
  }

  &__error-title {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-background));
    margin: 0;
  }

  &__error-message {
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-background), 0.6);
    margin: 0;
    line-height: 1.5;
  }

  &__retry-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 140px;
    padding: 14px 28px;
    margin-top: 8px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary, var(--v-theme-primary)))
    );
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__retry-spinner {
    animation: app-loading-spin 0.8s linear infinite;
  }
}

@keyframes app-loading-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
